import { Vector2 } from 'three'
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js'

/**
 * Final grade: chromatic aberration, vignette, film grain and a cold colour
 * push. One pass rather than four, because these are all cheap per-pixel
 * operations and chaining separate passes would mean three extra full-screen
 * blits for no visual gain.
 *
 * The grain is animated hash noise rather than a texture — it costs nothing and
 * never tiles.
 */
const GradeShader = {
  name: 'GradeShader',
  uniforms: {
    tDiffuse: { value: null },
    uTime: { value: 0 },
    uResolution: { value: new Vector2(1, 1) },
    // These three used to fight each other: heavy grain plus a strong vignette
    // plus aberration turned an already dark scene into mud, and obstacles and
    // the platform edge disappeared into it. Grain is now a hint of texture
    // rather than a filter, and the vignette only softens the corners.
    uAberration: { value: 0.0007 },
    uVignette: { value: 0.5 },
    uGrain: { value: 0.01 },
    /** Extra punch during hits: pushed up briefly by the camera shake. */
    uImpact: { value: 0 },
    uSaturation: { value: 0.92 },
    /**
     * Lifts the deepest shadows just enough to keep geometry as shape.
     * Kept small: at 0.035 it turned the void behind the platform into milky
     * grey and the whole frame read as veiled.
     */
    uLift: { value: 0.012 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform vec2  uResolution;
    uniform float uAberration;
    uniform float uVignette;
    uniform float uGrain;
    uniform float uImpact;
    uniform float uSaturation;
    uniform float uLift;
    varying vec2 vUv;

    float hash(vec2 p) {
      p = fract(p * vec2(123.34, 345.45));
      p += dot(p, p + 34.345);
      return fract(p.x * p.y);
    }

    void main() {
      vec2 uv = vUv;
      vec2 centered = uv - 0.5;
      float r2 = dot(centered, centered);

      // Aberration grows toward the edges, and spikes on impact.
      float amount = uAberration * (1.0 + r2 * 3.0) * (1.0 + uImpact * 6.0);
      vec2 dir = normalize(centered + 1e-6);
      vec3 color;
      color.r = texture2D(tDiffuse, uv + dir * amount).r;
      color.g = texture2D(tDiffuse, uv).g;
      color.b = texture2D(tDiffuse, uv - dir * amount).b;

      // Cold, desaturated grade: green shadows, bleached highlights.
      float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      color = mix(vec3(luma), color, uSaturation);
      color *= vec3(0.96, 1.02, 0.98);

      // Shadow lift: a filmic toe that keeps dark geometry as *shape* instead
      // of flat black, without touching the midtones or highlights.
      color = color + uLift * (1.0 - smoothstep(0.0, 0.16, luma));
      color = pow(color, vec3(0.97, 0.95, 0.99));
      color += vec3(0.006, 0.010, 0.008) * (1.0 - luma);

      // Vignette.
      float vig = 1.0 - uVignette * r2 * (1.0 + uImpact);
      color *= clamp(vig, 0.0, 1.0);

      // Animated grain, stronger in the shadows where film grain actually lives.
      // Deliberately gentle: it should be felt, not seen.
      float grain = hash(uv * uResolution + fract(uTime) * 977.0) - 0.5;
      color += grain * uGrain * (1.0 - luma * 0.7);

      // A hit briefly blows out the whole frame.
      color += uImpact * 0.12;

      gl_FragColor = vec4(color, 1.0);
    }
  `,
}

export class GradePass extends ShaderPass {
  constructor() {
    super(GradeShader)
  }

  override setSize(width: number, height: number): void {
    super.setSize(width, height)
    this.uniforms.uResolution.value.set(width, height)
  }

  update(elapsed: number, impact: number): void {
    this.uniforms.uTime.value = elapsed
    this.uniforms.uImpact.value = impact
  }
}
